import React, { useEffect, useState } from "react"
import { Calendar, Views, dateFnsLocalizer } from "react-big-calendar"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "moment-timezone"
import { useMemo } from "react"
import format from "date-fns/format"
import parse from "date-fns/parse"
import startOfDay from "date-fns/startOfDay"
import startOfWeek from "date-fns/startOfWeek"
import getDay from "date-fns/getDay"
import "../../shared/NCCalendar.scss"
import { useTranslation } from "react-i18next"

const locales = {
  "en-GB": require("date-fns/locale/en-IN")
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfDay,
  startOfWeek,
  getDay,
  locales
})

// ---- Timezone helpers (Intl-based, no extra dependency) ----
const getTzParts = (date, tz) => {
  const parts = {}
  new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  })
    .formatToParts(date)
    .forEach(p => {
      if (p.type !== "literal") parts[p.type] = parseInt(p.value, 10)
    })
  if (parts.hour === 24) parts.hour = 0
  return parts
}

// A UTC instant → a Date whose browser-local wall clock equals the driver's
// local time in `tz` (so react-big-calendar renders driver-local times).
const utcToLocalDate = (utcDate, tz) => {
  const p = getTzParts(utcDate, tz)
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
}

const AppointmentCalendar = ({ activities, getInterval, timezone }) => {
  const [events, setEvents] = useState([])
  const [startDate, setStartDate] = useState(new Date())

  const { t } = useTranslation();

  useEffect(() => {
    if (activities) {
      const activitiesWithTitle = activities.map(activity => {
        if (activity.trip) {
          activity.title = `${t("appointments.ride_planned_for")} ${activity?.client_name} ${t("appointments.by_driver")} ${activity?.driver?.name}.`
        } else {
          activity.title = `${t("appointments.driver")} ${activity?.driver_details?.name} ${t("appointments.availability")}.`
        }
        
        // Handle start_time - use 'start' field from new availability system or 'start_time' from old
        const startValue = activity.start || activity.start_time
        const endValue = activity.end || activity.end_time

        // Availability events come from the backend as UTC ('...Z'). Parse them
        // as naive local time (strip the Z) so they stay on the stored calendar
        // day in every view — otherwise a Monday UTC slot renders on Tuesday in
        // UTC+ timezones (Sri Lanka +5:30). Trip events carry `trip` and use
        // real timezone-aware datetimes, so they're parsed normally.
        const isAvailability = !activity.trip

        // Parse dates and ensure they're on the same day
        let startDate, endDate
        if (isAvailability) {
          if (timezone) {
            // Backend sends the slot's UTC time-of-day on the calendar date
            // (e.g. "2026-08-14T20:02:00Z"). Convert the UTC TIME to the
            // driver's timezone but keep it on the SAME calendar date — so a
            // Friday slot stays on Friday and matches the Add page exactly
            // (a 20:02 UTC slot shows as 01:02 AM on Friday, not Sat).
            const convertSlot = dt => {
              const base = new Date(dt)
              const p = getTzParts(
                new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(), base.getUTCHours(), base.getUTCMinutes())),
                timezone
              )
              return new Date(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(), p.hour, p.minute)
            }
            startDate = convertSlot(activity.start_time)
            endDate = convertSlot(activity.end_time)
          } else {
            // Fallback: naive local (strip Z) keeps the stored day in every view
            startDate = new Date(String(activity.start_time).replace(/Z$/, ""))
            endDate = new Date(String(activity.end_time).replace(/Z$/, ""))
          }
        } else {
          // Trips carry real absolute datetimes. Show them in the driver's tz
          // too when we have it — build a browser-local Date matching the
          // driver's wall clock so the calendar renders driver-local times.
          const baseStart = new Date(startValue)
          const baseEnd = new Date(endValue)
          if (timezone) {
            startDate = utcToLocalDate(baseStart, timezone)
            endDate = utcToLocalDate(baseEnd, timezone)
          } else {
            startDate = baseStart
            endDate = baseEnd
          }
        }

        // Cross-midnight availability slot (end before start): clamp to end of
        // the start day so it renders as a single-day event on the right day
        if (isAvailability && endDate < startDate) {
          endDate = new Date(startDate)
          endDate.setHours(23, 59, 59, 999)
        } else if (startDate.toDateString() !== endDate.toDateString()) {
          // If end date is on a different day, set it to 11:59 PM of the start day
          endDate = new Date(startDate)
          endDate.setHours(23, 59, 59, 999)
        }

        activity.start = startDate
        activity.end = endDate
        // Don't overwrite start_time/end_time (raw API strings) — the effect
        // re-runs whenever activities changes and converts from those ORIGINAL
        // values. Only 'start'/'end' are consumed by the calendar (accessors).
        
        // Mark as non-all-day event
        activity.allDay = false
          
        return activity
      })
      setEvents(activitiesWithTitle)
    }
  }, [activities, t, timezone])
  
  const { views } = useMemo(
    () => ({
      views: [Views.DAY, Views.WEEK, Views.MONTH]
    }),
    []
  )
  
  const onNavigate = (date, view) => {
    let stringDate = format(new Date(date), "yyyy-MM-dd")
    setStartDate(stringDate)
    getInterval(stringDate, view)
  }

  const onView = view => {
    let stringDate = format(new Date(startDate), "yyyy-MM-dd")
    getInterval(stringDate, view)
  }

  const eventPropGetter = data => {
    const newStyle = {
      border: "0px",
      fontSize: "10px"
    }
    if (data.trip) {
      newStyle.backgroundColor = "#3174ad"
    } else {
      newStyle.backgroundColor = "#D1FCF5"
      newStyle.color = "black"
    }
    return {
      style: newStyle
    }
  }

  return (
    <div>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        defaultView={Views.DAY}
        style={{
          height: "650px",
          width: "auto",
          marginTop: "15px",
          marginBottom: "15px"
        }}
        views={views}
        eventPropGetter={eventPropGetter}
        onView={onView}
        onNavigate={onNavigate}
      />
    </div>
  )
}

export default AppointmentCalendar
